import { QueryClientProvider } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { queryClient } from "@/api/queryClient";
import { ToastCustom } from "@/components/molecules";
import { Layout } from "@/components/templates";
import { ErrorBoundary } from "@/components/units";
import { AbilityProvider } from "@/services/casl/AbilityProvider.tsx";
import { rootStore } from "@/store/RootStore";
import { StoreProvider } from "@/store/StoreContext";

import { Loader } from "./components/atoms";
import {
  ERROR_ROUTE_CONFIG,
  getRouteConfig,
  type RouteConfig,
} from "./config/routes.tsx";
import { AuthInitializer } from "./services/auth/AuthInitializers.tsx";
import { IntercomProvider } from "./services/intercom/IntercomProvider.tsx";
import { initSentry } from "./services/sentry/initSentry.ts";

const renderNestedRoutes = (
  routes: RouteConfig[],
  keyPrefix = ""
): React.ReactNode => {
  return routes.map((route, index) => {
    const key = `${keyPrefix}-${index}`;
    if (route.index === true) {
      return <Route key={key} index element={route.element} />;
    }
    return (
      <Route key={key} path={route.path} element={route.element}>
        {route.children && renderNestedRoutes(route.children, key)}
      </Route>
    );
  });
};

const AppContent = observer(() => {
  useEffect(() => {
    if (!rootStore.authStore.isInitialized) {
      return;
    }
  }, [rootStore.authStore.isInitialized, rootStore.authStore.isAdmin]);

  useEffect(() => {
    initSentry();
  }, []);

  return (
    <ErrorBoundary>
      <StoreProvider store={rootStore}>
        <AbilityProvider>
          <IntercomProvider />
          <AuthInitializer>
            <BrowserRouter>
              <Suspense fallback={<Loader fullscreen />}>
                <ToastCustom />
                <Routes>
                  <Route element={<Layout />}>
                    {getRouteConfig().map((route, i) => (
                      <Route key={i} path={route.path} element={route.element}>
                        {route.children &&
                          renderNestedRoutes(route.children, `route-${i}`)}
                      </Route>
                    ))}
                  </Route>

                  {ERROR_ROUTE_CONFIG.map((route, i) => (
                    <Route key={i} path={route.path} element={route.element} />
                  ))}
                </Routes>
              </Suspense>
            </BrowserRouter>
          </AuthInitializer>
        </AbilityProvider>
      </StoreProvider>
    </ErrorBoundary>
  );
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
};

export default App;
